import type { StatsPeriod } from '@/shared/lib'

/**
 * Everything that decides *which* rows the table is showing, minus the page
 * number.
 *
 * It lives in the URL in one piece: a filtered log is worth sending to someone,
 * and «вот этот посетитель» is a link or it is nothing. The routes declare the
 * zod for it inline — `validateSearch` runs eagerly, so importing a runtime
 * value here would pin the whole lazy page into the initial bundle — and check
 * the result against this type with `satisfies`, which is what keeps the two
 * copies from drifting.
 *
 * `served` rather than `page`: the route's `page` is already the page number.
 */
export type LeadSlice = {
  period: StatsPeriod
  /** IP, exact or the start of a subnet. */
  q: string
  /*
   * Lists, not single values: «боты или абузы» is one question, and the reader
   * asking it should not have to run the table twice. Empty means «не
   * фильтровать» — the backend reads an absent parameter the same way.
   *
   * In the URL each one is a comma-joined string (`?type=bot,lead`), which is
   * both what the backend parses and what a person can edit by hand. The routes
   * do that conversion, so nothing downstream of them sees a string.
   */
  type: string[]
  served: string[]
  country: string[]
  source: string[]
}

/** The slice with nothing chosen — «всё за неделю». */
export const EMPTY_SLICE: Omit<LeadSlice, 'period'> = {
  q: '',
  type: [],
  served: [],
  country: [],
  source: [],
}

/** The slice as the URL spells it: multi-value filters comma-joined. */
export type LeadSliceSearch = Omit<LeadSlice, 'type' | 'served' | 'country' | 'source'> & {
  type: string
  served: string
  country: string
  source: string
}

const LIST_KEYS = ['type', 'served', 'country', 'source'] as const

/**
 * URL → slice. An absent or empty parameter is «не фильтровать».
 *
 * The route calls this and so does nothing else: the conversion exists once, so
 * the URL and the query key cannot disagree about what `?type=bot,lead` means.
 *
 * Every field is named rather than spread, and that is load-bearing. A route's
 * search carries more than the slice — the open view, the page, the selected
 * lead — and this result goes on to become a query key. Spreading let `view`
 * through once, and the loader then preloaded a key the table never asks for:
 * a request computed, fetched and thrown away, which on screen is
 * indistinguishable from having written no loader at all. Naming the fields
 * makes anything extra impossible to leak.
 */
export function sliceFromSearch(search: LeadSliceSearch): LeadSlice {
  return {
    period: search.period,
    q: search.q,
    type: splitValues(search.type),
    served: splitValues(search.served),
    country: splitValues(search.country),
    source: splitValues(search.source),
  }
}

/** Slice patch → URL patch. An empty list becomes '' and drops out of the query. */
export function slicePatchToSearch(patch: Partial<LeadSlice>): Partial<LeadSliceSearch> {
  const out: Record<string, unknown> = { ...patch }
  for (const key of LIST_KEYS) {
    const values = patch[key]
    if (values !== undefined) out[key] = values.join(',')
  }
  return out as Partial<LeadSliceSearch>
}

const splitValues = (raw: string): string[] => (raw === '' ? [] : raw.split(','))
